import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Badge, Drawer, List, Typography, Tag, Empty, Button, Tooltip, Tabs } from "antd";
import { 
  BellOutlined, RightOutlined, ClockCircleOutlined, CalendarOutlined,
  UnlockOutlined, CheckCircleOutlined, SwapOutlined, TeamOutlined,
  HistoryOutlined, NotificationOutlined, TagOutlined, LogoutOutlined,
  FundProjectionScreenOutlined, FileDoneOutlined, SyncOutlined, DollarOutlined,
  CheckOutlined, DeleteOutlined, InboxOutlined, GiftOutlined, AuditOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import axios from "axios";
import { useNavigation } from "../context/NavigationContext";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

/* ─────────────────────────────────────────────────────────────────
   ROBUST LINK RESOLUTION WITH ROLE-BASED ROUTING
────────────────────────────────────────────────────────────────── */

function extractIdFromLink(link) {
  if (!link || typeof link !== "string") return null;
  const match = link.match(/\/(\d+)\/?$/);
  return match ? match[1] : null;
}

function resolveLink(link, message = "", authData = null) {
  if (!link || typeof link !== "string") return null;
  const normalized = link.toLowerCase().trim();
  const msgLower = (message || "").toLowerCase();
  const id = extractIdFromLink(link);

  const isApprovalRequest = msgLower.includes("action required") ||
    (msgLower.includes("approve") && !msgLower.includes("approved")) ||
    msgLower.includes("waiting for your approval") ||
    msgLower.includes("pending your review") ||
    msgLower.includes("request raised") ||
    (msgLower.includes("new") && msgLower.includes("request"));

  const isStatusUpdate = msgLower.includes("approved") ||
    msgLower.includes("rejected") ||
    msgLower.includes("has been") ||
    msgLower.includes("update:");

  // LEAVE
  if (normalized.includes("leave")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "leaveRequests", params: { leaveId: id, openModal: true, status: "Pending" } };
    return { menuKey: "leaves", params: { leaveId: id, openModal: true, status: targetStatus } };
  }
  // PERMISSION
  if (normalized.includes("permission")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "permissionRequests", params: { permissionId: id, openModal: true, status: "Pending" } };
    return { menuKey: "permissions", params: { permissionId: id, openModal: true, status: targetStatus } };
  }
  // ON-DUTY
  if (normalized.includes("on-duty") || normalized.includes("onduty")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "onduty", params: { status: "Pending" } };
    return { menuKey: "appliedOnDuty", params: { status: targetStatus } };
  }
  // TRANSFER
  if (normalized.includes("transfer") || normalized.includes("/payroll/super")) {
    if (msgLower.includes("you have been transferred") || msgLower.includes("your transfer")) {
      return { menuKey: "profile", params: null };
    }
    const targetStatus = msgLower.includes("approved") ? "APPROVED" : (msgLower.includes("rejected") ? "REJECTED" : "PENDING");
    return { menuKey: "empTransfer", params: { status: targetStatus } };
  }
  // RFC
  if (normalized.includes("rfc")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : "Pending";
    return { menuKey: authData?.employee?.is_hr ? "rfc" : "myrfc", params: { status: targetStatus } };
  }
  // TICKET
  if (normalized.includes("ticket")) {
    return { menuKey: isApprovalRequest ? "tickets" : "viewTicket", params: { status: "NEW" } };
  }
  // PROFILE
  if (normalized.includes("/profile")) return { menuKey: "profile", params: null };

  return null;
}

function getTagProps(link, message = "") {
  const normalized = (link || "").toLowerCase();
  const msgLower = (message || "").toLowerCase();
  if (normalized.includes("leave")) return { color: "green", label: "Leave", icon: <CalendarOutlined /> };
  if (normalized.includes("permission")) return { color: "purple", label: "Permission", icon: <UnlockOutlined /> };
  if (normalized.includes("transfer")) return { color: "volcano", label: "Transfer", icon: <SwapOutlined /> };
  if (normalized.includes("payroll") || msgLower.includes("salary")) return { color: "green", label: "Payroll", icon: <DollarOutlined /> };
  if (msgLower.includes("birthday") || msgLower.includes("wish")) return { color: "magenta", label: "Celebration", icon: <GiftOutlined /> };
  return { color: "blue", label: "Notification", icon: <BellOutlined /> };
}

const NotificationBar = ({ backUrl, authData }) => {
  const [notifications, setNotifications] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { navigateTo } = useNavigation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${authData?.access}` }), [authData]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${backUrl}/api/probation/notifications/?t=${Date.now()}`, { headers });
      setNotifications(res.data?.results || res.data || []);
    } catch (error) { console.error(error); }
  }, [backUrl, headers]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try {
      await axios.post(`${backUrl}/api/probation/notifications/${id}/mark-as-read/`, {}, { headers });
    } catch (err) { console.error(err); }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await Promise.all(unreadIds.map(id => axios.post(`${backUrl}/api/probation/notifications/${id}/mark-as-read/`, {}, { headers })));
    } catch (err) { console.error(err); }
  };

  const handleNotificationClick = async (item) => {
    if (!item.is_read) await markAsRead(item.id);
    const resolved = resolveLink(item.link, item.message, authData);
    if (!resolved?.menuKey) { setDrawerVisible(false); return; }

    const currentPath = window.location.pathname.toLowerCase();
    let targetDashboardUrl = "/employee";
    const isHrUser = authData?.employee?.is_hr || authData?.employee?.is_superuser;
    const isHodUser = authData?.employee?.is_hod || authData?.employee?.team_count > 0;
    const msgLower = (item.message || "").toLowerCase();
    const linkStr = (item.link || "").toLowerCase();

    if (isHrUser && (linkStr.includes("/hr/") || linkStr.includes("/payroll/"))) {
      targetDashboardUrl = linkStr.includes("/payroll/") ? "/payroll_hr" : "/hrdashboard";
    } else if (isHodUser && (linkStr.includes("/hod/") || msgLower.includes("approval"))) {
      targetDashboardUrl = "/hod";
    }

    if (resolved.menuKey === "profile" || resolved.menuKey === "home") {
      if (currentPath.includes("/hrdashboard")) targetDashboardUrl = "/hrdashboard";
      else if (currentPath.includes("/hod")) targetDashboardUrl = "/hod";
      else if (currentPath.includes("/payroll_hr")) targetDashboardUrl = "/payroll_hr";
    }

    if (targetDashboardUrl && !currentPath.includes(targetDashboardUrl)) {
      if (resolved.params) localStorage.setItem("bemss_navigation_params", JSON.stringify(resolved.params));
      const dashboardKey = targetDashboardUrl === "/hrdashboard" ? "hrDashboardPage" : (targetDashboardUrl === "/hod" ? "hodDashboardPage" : (targetDashboardUrl === "/payroll_hr" ? "payrollHrPage" : "employeePage"));
      localStorage.setItem(dashboardKey, resolved.menuKey);
      window.location.href = targetDashboardUrl;
    } else {
      setDrawerVisible(false);
      navigateTo(resolved.menuKey, resolved.params);
    }
  };

  const groupedNotifications = useMemo(() => {
    const list = activeTab === "unread" ? notifications.filter(n => !n.is_read) : notifications;
    const groups = { today: [], yesterday: [], earlier: [] };
    list.forEach(n => {
      const d = dayjs(n.created_at);
      if (d.isToday()) groups.today.push(n);
      else if (d.isYesterday()) groups.yesterday.push(n);
      else groups.earlier.push(n);
    });
    return groups;
  }, [notifications, activeTab]);

  const renderList = (items) => (
    <List dataSource={items} split={false} renderItem={(item) => {
      const tagProps = getTagProps(item.link, item.message);
      return (
        <div className="premium-notif-card" onClick={() => handleNotificationClick(item)} style={{
          padding: "16px", marginBottom: "12px", borderRadius: "16px", cursor: "pointer",
          backgroundColor: item.is_read ? "rgba(255,255,255,0.6)" : "#ffffff",
          border: item.is_read ? "1px solid rgba(0,0,0,0.05)" : "1px solid rgba(37, 99, 235, 0.15)",
          boxShadow: item.is_read ? "none" : "0 8px 20px rgba(37, 99, 235, 0.08)",
          position: "relative", transition: "all 0.3s ease", backdropFilter: "blur(10px)", display: "flex", gap: "14px"
        }}>
          {!item.is_read && <div style={{ position: "absolute", top: 12, right: 12, width: 8, height: 8, background: "#2563eb", borderRadius: "50%", boxShadow: "0 0 8px rgba(37, 99, 235, 0.6)" }} />}
          <div style={{ width: 42, height: 42, borderRadius: 12, background: item.is_read ? "#f1f5f9" : "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: item.is_read ? "#94a3b8" : "#2563eb", flexShrink: 0 }}>
            {tagProps?.icon || <BellOutlined />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <Typography.Text strong style={{ fontSize: 11, color: item.is_read ? "#94a3b8" : "#1e293b", textTransform: "uppercase", letterSpacing: 1 }}>{tagProps?.label}</Typography.Text>
              <Typography.Text style={{ fontSize: 10, color: "#94a3b8" }}>{dayjs(item.created_at).fromNow()}</Typography.Text>
            </div>
            <Typography.Text style={{ fontSize: 13.5, color: item.is_read ? "#94a3b8" : "#334155", lineHeight: 1.5, display: "block", fontWeight: item.is_read ? 400 : 500 }}>{item.message}</Typography.Text>
          </div>
        </div>
      );
    }} />
  );

  return (
    <>
      <style>{`
        .premium-notif-card:hover { transform: translateY(-2px); box-shadow: 0 12px 30px rgba(0,0,0,0.1) !important; }
        .glass-drawer .ant-drawer-content { background: rgba(255, 255, 255, 0.75) !important; backdrop-filter: blur(25px) !important; }
        .glass-drawer .ant-drawer-header { background: transparent !important; border: none !important; }
        .section-label { font-size: 10px; font-weight: 800; color: #94a3b8; text-transform: uppercase; margin: 20px 0 10px 4px; display: block; letter-spacing: 1.5px; }
      `}</style>
      <Tooltip title="Notifications">
        <div style={{ position: "fixed", bottom: 40, right: 40, zIndex: 2000, background: "#1e293b", color: "white", width: 56, height: 56, borderRadius: 18, boxShadow: "0 10px 25px rgba(0,0,0,0.2)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s ease" }}
          onClick={() => setDrawerVisible(true)} onMouseEnter={e => e.currentTarget.style.transform = "translateY(-4px)"} onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
          <Badge count={notifications.filter(n => !n.is_read).length} size="small" offset={[-2, 2]} style={{ backgroundColor: "#ef4444" }}>
            <BellOutlined style={{ fontSize: 24, color: "white" }} />
          </Badge>
        </div>
      </Tooltip>
      <Drawer className="glass-drawer" title={<div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
        <Typography.Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Notification Center</Typography.Title>
        <Button type="text" size="small" onClick={markAllAsRead} style={{ color: "#2563eb", fontWeight: 700 }}>Mark all as read</Button>
      </div>} placement="right" width={420} onClose={() => setDrawerVisible(false)} open={drawerVisible} bodyStyle={{ padding: "0 24px" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[{ key: "all", label: `All (${notifications.length})` }, { key: "unread", label: `New (${notifications.filter(n => !n.is_read).length})` }]} />
        {notifications.length === 0 ? <Empty style={{ marginTop: 100 }} description="All caught up!" /> : (
          <div style={{ paddingBottom: 40 }}>
            {groupedNotifications.today.length > 0 && <><span className="section-label">Today</span>{renderList(groupedNotifications.today)}</>}
            {groupedNotifications.yesterday.length > 0 && <><span className="section-label">Yesterday</span>{renderList(groupedNotifications.yesterday)}</>}
            {groupedNotifications.earlier.length > 0 && <><span className="section-label">Earlier</span>{renderList(groupedNotifications.earlier)}</>}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default NotificationBar;
